import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { GOOGLE_PLACE_CONFIG } from '../config/googlePlaces';

const API_BASE = window.location.origin || '';

// Google Places API key for frontend (with HTTP referer restriction)
const GOOGLE_PLACES_API_KEY = 'AIzaSyBckvMLaIsHdpAMtLok_0dIbYyq_Gebq2Y';
const GOOGLE_PLACE_ID = GOOGLE_PLACE_CONFIG.placeId;

const ReviewsSection = () => {
  const carouselRef = useRef(null);
  const [carouselHeight, setCarouselHeight] = useState('auto');
  
  // Static fallback reviews (shown when no Google reviews available)
  const staticReviews = [
    {
      name: 'Nassim',
      location: 'Algeria',
      rating: 5,
      text: 'GoTurkeyTransfer provided excellent service. They were very professional, responded quickly through WhatsApp, guided us with the booking process, and arranged a driver immediately. Prices are reasonable, and we highly recommend them.',
      highlights: ['excellent service', 'professional', 'WhatsApp'],
      source: 'TripAdvisor',
      route: 'IST → Hotel',
      date: 'Sep 2025'
    },
    {
      name: 'Serkan S',
      location: 'Turkey',
      rating: 5,
      text: 'It is a luxury vehicle. Vehicle treats are of high quality. Our driver was very polite and paid attention to driving safety. It was a safe and luxurious transfer.',
      highlights: ['luxury vehicle', 'high quality', 'safe'],
      source: 'TripAdvisor',
      route: 'IST → City',
      date: 'Jan 2025'
    },
    {
      name: 'Aidan S',
      location: 'Ireland',
      rating: 5,
      text: 'They picked me up from Istanbul Airport and dropped me off at my hotel. During my stay, they also assisted me in visiting my clients. I used their services multiple times, and everything was seamless, from the cars to the communication.',
      highlights: ['seamless', 'multiple times', 'communication'],
      source: 'TripAdvisor',
      route: 'IST → Hotel',
      date: 'Apr 2024'
    },
    {
      name: 'Koray S',
      location: 'Turkey',
      rating: 5,
      text: 'Thank you very much to Mr. Kemal who brought my daughter safely from Dalaman airport to Fethiye. My daughter was very happy with the treats, TV and WiFi provided in the vehicle. From now on, our first choice will always be GoTurkeyTransfer Transfer.',
      highlights: ['safely', 'treats', 'TV and WiFi'],
      source: 'TripAdvisor',
      route: 'DLM → Fethiye',
      date: 'Aug 2022'
    },
  ];

  const [reviews, setReviews] = useState(staticReviews);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [googleRating, setGoogleRating] = useState(GOOGLE_PLACE_CONFIG.rating);
  const [totalReviews, setTotalReviews] = useState(GOOGLE_PLACE_CONFIG.totalReviews);
  
  // Fetch Google reviews directly from Google Places API
  useEffect(() => {
    const fetchGoogleReviews = async () => {
      try {
        // Use Google Places API directly (works with referer-restricted keys)
        const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${GOOGLE_PLACE_ID}&fields=name,rating,user_ratings_total,reviews&key=${GOOGLE_PLACES_API_KEY}&language=en&reviews_sort=newest`;
        
        // Note: Direct fetch from browser may be blocked by CORS
        // Using a proxy or backend call is recommended for production
        const response = await fetch(`${API_BASE}/api/places/reviews?language=en`);
        const data = await response.json();
        
        if (data.success && data.data?.reviews?.length > 0) {
          // Format Google reviews
          const googleReviews = data.data.reviews
            .filter(review => review.rating >= 4) // Only show 4+ star reviews
            .map(review => ({
              name: review.author_name,
              rating: review.rating,
              text: review.text,
              route: '',
              date: review.relative_time,
              source: 'Google',
              profilePhoto: review.profile_photo_url,
              highlights: extractHighlights(review.text),
              isGoogle: true
            }));
          
          // Update rating info
          if (data.data.rating) {
            setGoogleRating(data.data.rating);
          }
          if (data.data.user_ratings_total) {
            setTotalReviews(data.data.user_ratings_total);
          }
          
          // Prioritize Google reviews, fallback to static
          if (googleReviews.length >= 3) {
            setReviews(googleReviews);
          } else {
            setReviews([...googleReviews, ...staticReviews.slice(0, 4 - googleReviews.length)]);
          }
          
          console.log(`Loaded ${googleReviews.length} Google reviews`);
          return;
        }
      } catch (error) {
        console.log('Google Places API unavailable, trying internal reviews:', error.message);
      }
      
      // Fallback: Try internal feedback reviews
      try {
        const response = await fetch(`${API_BASE}/api/feedback/public/reviews?limit=10`);
        const data = await response.json();
        
        if (data.success && data.reviews && data.reviews.length > 0) {
          const dynamicReviews = data.reviews.map(review => ({
            name: review.name,
            rating: review.rating,
            text: review.text,
            route: review.route || '',
            date: review.date || '',
            source: 'Verified Customer',
            highlights: extractHighlights(review.text),
            isDynamic: true
          }));
          
          if (dynamicReviews.length >= 4) {
            setReviews(dynamicReviews);
          } else {
            setReviews([...dynamicReviews, ...staticReviews.slice(0, 4 - dynamicReviews.length)]);
          }
        }
      } catch (error) {
        console.log('Using static reviews:', error.message);
      }
    };

    fetchGoogleReviews();
  }, []);

  // Calculate carousel height based on visible card (mobile only)
  useEffect(() => {
    const updateCarouselHeight = () => {
      if (!carouselRef.current) return;
      
      // Only apply on mobile (< 768px)
      if (window.innerWidth >= 768) {
        setCarouselHeight('auto');
        return;
      }
      
      const cards = carouselRef.current.querySelectorAll('.review-card');
      if (cards[currentIndex]) {
        const cardHeight = cards[currentIndex].offsetHeight;
        setCarouselHeight(`${cardHeight}px`);
      }
    };

    updateCarouselHeight();
    window.addEventListener('resize', updateCarouselHeight);
    return () => window.removeEventListener('resize', updateCarouselHeight);
  }, [currentIndex, reviews]);

  // Extract keywords to highlight from review text
  const extractHighlights = (text) => {
    if (!text) return [];
    const keywords = ['excellent', 'professional', 'safe', 'clean', 'comfortable', 'punctual', 
                      'friendly', 'luxury', 'amazing', 'great', 'perfect', 'recommend', 
                      'on time', 'helpful', 'smooth', 'reliable'];
    const found = [];
    const lowerText = text.toLowerCase();
    keywords.forEach(keyword => {
      if (lowerText.includes(keyword) && found.length < 3) {
        found.push(keyword);
      }
    });
    return found;
  };

  // Use state for itemsPerPage to avoid hydration mismatch
  // Server always renders with default value (2), then client updates if needed
  const [itemsPerPage, setItemsPerPage] = useState(2);
  
  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(window.innerWidth < 768 ? 1 : 2);
    };
    
    // Set initial value on client
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const maxIndex = Math.max(0, reviews.length - itemsPerPage);

  const next = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const prev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-primary fill-primary' : 'text-muted-foreground'}`}
      />
    ));
  };

  // Function to highlight keywords in orange
  const highlightText = (text, highlights) => {
    let result = text;
    highlights.forEach((phrase) => {
      const regex = new RegExp(`(${phrase})`, 'gi');
      result = result.replace(regex, '<span class="text-primary font-semibold">$1</span>');
    });
    return result;
  };

  return (
    <section id="reviews" className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-16">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Customer Reviews
            </h2>
            <p className="text-muted-foreground max-w-xl">
              See what our customers say about their GoTurkeyTransfer experience
            </p>
          </div>

          {/* Rating Summary */}
          <div className="mt-6 md:mt-0 flex items-center gap-6">
            <div className="flex items-center gap-4">
              <a 
                href={GOOGLE_PLACE_CONFIG.writeReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-center hover:scale-105 transition-transform"
                data-testid="google-reviews-link"
              >
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 text-primary fill-primary" />
                  <span className="font-display text-2xl font-bold text-foreground">{googleRating}</span>
                </div>
                <span className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  Google ({totalReviews}+)
                </span>
              </a>
              <div className="w-px h-10 bg-border"></div>
              <a 
                href="https://www.tripadvisor.com.tr/Attraction_Review-g293974-d34229539-Reviews-Go_Turkey_Transfer-Istanbul.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-center hover:scale-105 transition-transform"
                data-testid="tripadvisor-reviews-link"
              >
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 text-primary fill-primary" />
                  <span className="font-display text-2xl font-bold text-foreground">5.0</span>
                </div>
                <span className="text-xs text-muted-foreground hover:text-primary transition-colors">TripAdvisor</span>
              </a>
            </div>
          </div>
        </div>

        {/* Reviews Carousel */}
        <div className="relative">
          {/* Mobile: Show only current card */}
          <div className="block md:hidden">
            {reviews[currentIndex] && (
              <div className="px-3">
                <Card className="bg-secondary border-border">
                  <CardContent className="p-6">
                    {/* Quote Icon */}
                    <Quote className="h-8 w-8 text-primary/30 mb-4" />

                    {/* Review Text with Highlights */}
                    <p 
                      className="text-foreground/90 leading-relaxed mb-4"
                      dangerouslySetInnerHTML={{ 
                        __html: `"${highlightText(reviews[currentIndex].text, reviews[currentIndex].highlights)}"` 
                      }}
                    />

                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-4">
                      {renderStars(reviews[currentIndex].rating)}
                    </div>

                    {/* Reviewer Info */}
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="flex items-center gap-3">
                        {reviews[currentIndex].profilePhoto && (
                          <img 
                            src={reviews[currentIndex].profilePhoto} 
                            alt={reviews[currentIndex].name}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div>
                          <p className="font-semibold text-foreground">{reviews[currentIndex].name}</p>
                          <p className="text-sm text-muted-foreground">{reviews[currentIndex].location}</p>
                        </div>
                      </div>
                      {reviews[currentIndex].route && (
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Route</p>
                          <p className="text-sm font-medium text-primary">{reviews[currentIndex].route}</p>
                        </div>
                      )}
                    </div>

                    {/* Source Badge */}
                    <div className="mt-4 flex items-center">
                      {reviews[currentIndex].isGoogle ? (
                        <a 
                          href={GOOGLE_PLACE_CONFIG.viewReviewsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-colors"
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Google Review
                        </a>
                      ) : (
                        <a 
                          href="https://www.tripadvisor.com.tr/Attraction_Review-g293974-d34229539-Reviews-Go_Turkey_Transfer-Istanbul.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          via {reviews[currentIndex].source} ↗
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Desktop: Full carousel */}
          <div 
            className="hidden md:block overflow-hidden" 
            ref={carouselRef}
          >
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)` }}
            >
              {reviews.map((review, index) => (
                <div
                  key={index}
                  className="w-full md:w-1/2 flex-shrink-0 px-3 review-card"
                >
                  <Card className="bg-secondary border-border">
                    <CardContent className="p-6 md:p-8">
                      {/* Quote Icon */}
                      <Quote className="h-8 w-8 text-primary/30 mb-4" />

                      {/* Review Text with Highlights */}
                      <p 
                        className="text-foreground/90 leading-relaxed mb-4 md:mb-6 md:flex-1"
                        dangerouslySetInnerHTML={{ 
                          __html: `"${highlightText(review.text, review.highlights)}"` 
                        }}
                      />

                      {/* Rating */}
                      <div className="flex items-center gap-1 mb-4">
                        {renderStars(review.rating)}
                      </div>

                      {/* Reviewer Info */}
                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex items-center gap-3">
                          {/* Profile Photo for Google Reviews */}
                          {review.profilePhoto && (
                            <img 
                              src={review.profilePhoto} 
                              alt={review.name}
                              className="w-10 h-10 rounded-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <p className="font-semibold text-foreground">{review.name}</p>
                            <p className="text-sm text-muted-foreground">{review.location}</p>
                          </div>
                        </div>
                        {review.route && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Route</p>
                            <p className="text-sm font-medium text-primary">{review.route}</p>
                          </div>
                        )}
                      </div>

                      {/* Source Badge */}
                      <div className="mt-4 flex items-center">
                        {review.isGoogle ? (
                          <a 
                            href={GOOGLE_PLACE_CONFIG.viewReviewsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-colors"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Google Review
                          </a>
                        ) : (
                          <a 
                            href="https://www.tripadvisor.com.tr/Attraction_Review-g293974-d34229539-Reviews-Go_Turkey_Transfer-Istanbul.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                          >
                            via {review.source} ↗
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons - Reduced margin on mobile */}
          <div className="flex items-center justify-center gap-4 mt-4 md:mt-8">
            <button
              onClick={prev}
              disabled={currentIndex === 0}
              className="p-3 rounded-full bg-secondary border border-border text-foreground hover:bg-accent hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              aria-label="Previous review"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            {/* Road Lane Indicators */}
            <div className="flex items-center gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    i === currentIndex
                      ? 'w-8 bg-primary'
                      : 'w-4 bg-border hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              disabled={currentIndex >= maxIndex}
              className="p-3 rounded-full bg-secondary border border-border text-foreground hover:bg-accent hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              aria-label="Next review"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Call to Action - Leave a Google Review - Reduced margin on mobile */}
        <div className="mt-6 md:mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Had a great experience with us? We'd love to hear from you!
          </p>
          <a
            href={GOOGLE_PLACE_CONFIG.writeReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-full font-medium transition-all hover:scale-105"
            data-testid="leave-google-review-btn"
          >
            <Star className="h-5 w-5 fill-current" />
            Leave a Google Review
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;